// The shared audio blob pool — pure key arithmetic, no I/O.
//
// One R2 object per distinct byte string, keyed by the sha-256 of those bytes,
// referenced by any number of seeds and jam rooms. Before the pool every seed
// held a private copy under `<user>/<seed>/clips/`, so a jam publish copied
// every clip into the new version and a re-upload re-sent audio the server
// already had. Under the pool, identical bytes are stored once across every
// version, jam and user; the seed's arrangement and the room's clips carry the
// pool key as their `audioKey` exactly as they carried per-seed keys before.
//
// Lives in `shared/` because three writers mint or recognise pool keys: the
// API worker (uploads, jam files, publish, GC), the viz worker (publish,
// audit) and the extension (which pairs upload plans by hash). The channel
// prefix ("" stable, "beta/" beta) is applied server-side like every other
// key — a client sends a hash, never a key.

/** sha-256, 64 lowercase hex chars. Validated at every entry so a hash can
 * never smuggle path characters into an object key. */
export const BLOB_HASH_RE = /^[0-9a-f]{64}$/;

export const BLOB_PREFIX = "blobs/";

export function isBlobHash(value: unknown): value is string {
  return typeof value === "string" && BLOB_HASH_RE.test(value);
}

/** `<channelPrefix>blobs/<hash>.wav` */
export function poolKey(channelPrefix: string, hash: string): string {
  return `${channelPrefix}${BLOB_PREFIX}${hash}.wav`;
}

/** Whether `key` names an object in this channel's pool. */
export function isPoolKey(key: string, channelPrefix: string): boolean {
  return hashOfPoolKey(key, channelPrefix) !== null;
}

/** The hash a pool key names, or null for a legacy key / another channel. */
export function hashOfPoolKey(key: string, channelPrefix: string): string | null {
  const head = `${channelPrefix}${BLOB_PREFIX}`;
  if (!key.startsWith(head) || !key.endsWith(".wav")) return null;
  const hash = key.slice(head.length, -".wav".length);
  return BLOB_HASH_RE.test(hash) ? hash : null;
}

/**
 * The hash a pool key names in WHATEVER channel it is in — for a client,
 * which is handed keys but never told its channel prefix (it is whatever
 * precedes `blobs/`). Null for a legacy per-seed key or a local one.
 */
export function poolHashAnyChannel(key: string): string | null {
  const at = key.indexOf(BLOB_PREFIX);
  return at < 0 ? null : hashOfPoolKey(key, key.slice(0, at));
}

/** Hex encoding of a digest buffer (what DigestStream / subtle.digest yield). */
export function hexOf(digest: ArrayBuffer): string {
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * The S3 header a pool PUT carries. The API signs it into the presigned URL
 * (workers/src/storage.ts presignPoolPut), so the client cannot drop or change
 * it; R2 compares the body against it before the object exists and reports it
 * from `head()` afterwards; the web apps' object route forwards it. Together
 * that is the pool's proof of content — nothing has to read the bytes back.
 */
export const R2_CHECKSUM_HEADER = "x-amz-checksum-sha256";

/** The header's value for a hash: the digest bytes in base64 (S3's encoding, not hex). */
export function checksumHeaderValue(hash: string): string {
  if (!isBlobHash(hash)) throw new Error("checksumHeaderValue: not a blob hash");
  let bin = "";
  for (let i = 0; i < hash.length; i += 2) bin += String.fromCharCode(parseInt(hash.slice(i, i + 2), 16));
  return btoa(bin);
}

// ---- Preview renditions -----------------------------------------------------
//
// A preview is a small MP3 encoded from an original, stored as that original's
// SIBLING: `<key minus .wav>.mp3`. Pool objects get `blobs/<hash>.mp3`, legacy
// per-seed objects `<user>/<seed>/stems/<name>.mp3` and `.../master.mp3`. No
// row anywhere stores a preview key — it is arithmetic on the original's, and
// the `audio_previews` table (keyed by the ORIGINAL key) says whether one
// exists. Previews are never content-addressed: they are derived artifacts,
// verified by shape (workers/src/previews.ts), never handed out as "exists"
// on the strength of their bytes, and never imported.

export const PREVIEW_EXT = ".mp3";

/** `<key>.wav` → `<key>.mp3`, or null for a key that is not a `.wav`. */
export function previewKeyOf(key: string): string | null {
  if (!key.endsWith(".wav")) return null;
  return `${key.slice(0, -".wav".length)}${PREVIEW_EXT}`;
}

/** The original a preview key was derived from, or null for a non-preview key. */
export function originalOfPreviewKey(key: string): string | null {
  if (!key.endsWith(PREVIEW_EXT)) return null;
  const stem = key.slice(0, -PREVIEW_EXT.length);
  return stem.length > 0 ? `${stem}.wav` : null;
}

export function isPreviewKey(key: string): boolean {
  return originalOfPreviewKey(key) !== null;
}
